import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { RequestService, numericChainIdFromChainKey } from '../services/requestService';
import { ContractListener } from '../services/contractListener';
import init, { decrypt_ciphertext, decode_transfer_proof } from '../pkg/confidential_transfer_proof_generation.js';
import { CHAINS } from '../config/chains';
import { ipfsGatewayUrlForCid } from '../utils/ipfs';
import { formatPlainForDisplay, normalizeTokenDecimals, plainToUserAmountNumber } from '../utils/confidentialAmountScale.js';
import { fetchStellarTransferDecryptMaterial } from '../utils/sorobanTransferTxProof.js';
import './History.css';

/** EVM-only: Soroban facade has no `queryFilter` / block backfill. */
const contractSupportsEvmEventBackfill = (contract) =>
    Boolean(
        contract &&
            !contract.isStellarSoroban &&
            typeof contract.queryFilter === 'function' &&
            contract.provider &&
            typeof contract.provider.getBlockNumber === 'function'
    );

const resolveChainConfig = (chainKey) => {
    if (!chainKey) {
        return null;
    }

    if (CHAINS[chainKey]) {
        return CHAINS[chainKey];
    }

    const normalized =
        typeof chainKey === 'string' && chainKey.startsWith('0x')
            ? parseInt(chainKey, 16)
            : Number(chainKey);

    if (!Number.isNaN(normalized)) {
        return (
            Object.values(CHAINS).find((chain) => {
                if (chain.id === chainKey) return true;
                if (chain.chainId === chainKey) return true;
                if (typeof chain.chainIdDecimal === 'number' && chain.chainIdDecimal === normalized) return true;
                if (typeof chain.chainId === 'string') {
                    const chainHex = parseInt(chain.chainId, 16);
                    if (!Number.isNaN(chainHex) && chainHex === normalized) {
                        return true;
                    }
                }
                return false;
            }) || null
        );
    }

    return null;
};

const formatStatusLabel = (status) => {
    if (!status) return 'Pending';

    const normalized = status.toLowerCase();
    switch (normalized) {
        case 'success':
            return 'Completed';
        case 'failed':
            return 'Failed';
        case 'processing':
            return 'Processing';
        case 'pending':
            return 'Pending';
        default:
            return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    }
};

const formatHash = (hash) => {
    if (!hash || hash.length <= 12) {
        return hash || '';
    }
    return `${hash.slice(0, 10)}...${hash.slice(-6)}`;
};

const History = ({ userAccount, contract, provider, keypair, chainId }) => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [decryptingTxId, setDecryptingTxId] = useState(null);
    const [decryptedAmounts, setDecryptedAmounts] = useState({});
    const backfilledRef = useRef(new Set()); // Track which account+chain combos have been backfilled

    // Contract ABI for events
    const CONTRACT_ABI = [
        "event CreateAccountRequested(address indexed ownerAddr, uint256 indexed txId, bytes elgamalPubkey)",
        "event CreateAccountProcessed(address indexed ownerAddr, uint256 indexed txId, bool ok, string errorMsg, bytes elgamalPubkey)",
        "event DepositRequested(address indexed ownerAddr, uint256 indexed txId, address indexed token, uint256 plainAmount)",
        "event DepositProcessed(address indexed ownerAddr, uint256 indexed txId, bool ok, string errorMsg, address token, bytes newAvailC1, bytes newAvailC2, uint64 pendingCreditCounter, uint64 minimumPendingCreditCounter, bytes availableShare, bytes pendingShare)",
        "event TransferRequested(address indexed sender, address indexed recipient, uint256 indexed txId, address token, bytes proof, uint256 feePaid, bool useOffchainVerify, bytes senderPubkey, bytes recipientPubkey, bytes senderCurrC1, bytes senderCurrC2)",
        "event TransferProcessed(address indexed sender, address indexed recipient, uint256 indexed txId, bool ok, string errorMsg, address token, bytes senderNewAvailC1, bytes senderNewAvailC2, bytes recipientNewPendingC1, bytes recipientNewPendingC2, uint64 senderPendingCreditCounter, bytes senderAvailableShare, uint64 recipientPendingCreditCounter, uint64 recipientMinimumPendingCreditCounter, bytes recipientPendingShare)",
        "event ApplyPendingRequested(address indexed ownerAddr, uint256 indexed txId)",
        "event ApplyPendingProcessed(address indexed ownerAddr, uint256 indexed txId, bool ok, string errorMsg, address[] tokens, bytes[] newAvailC1, bytes[] newAvailC2, uint64 pendingCreditCounter, uint64 minimumPendingCreditCounter, bytes availableShare)",
        "event WithdrawRequested(address indexed ownerAddr, uint256 indexed txId, address token, uint256 plainAmount, bytes proof, bool useOffchainVerify, bytes userPubkey, bytes userCurrC1, bytes userCurrC2)",
        "event WithdrawProcessed(address indexed ownerAddr, uint256 indexed txId, bool ok, string errorMsg, address token, uint256 plainAmount, bytes newAvailC1, bytes newAvailC2, uint64 pendingCreditCounter, uint64 minimumPendingCreditCounter, bytes availableShare)"
    ];

    /** Stellar contract / account strkey: 56 chars, base32 (G or C prefix). */
    const isLikelyStellarStrkey = (s) => /^[CG][A-Z2-7]{55}$/i.test(String(s || '').trim());

    // Helper: resolve token symbol + decimals from CHAINS (must match TabbedOperations / wallet balance scaling).
    const getTokenInfoFromChain = (tokenIdentifier, reqChainId) => {
        const txChainConfig = resolveChainConfig(reqChainId || chainId);
        const fallbackUsdc = CHAINS.stellar?.tokens?.usdc;
        const defaultSymbol = fallbackUsdc?.symbol || 'USDC';
        const defaultDecimals = normalizeTokenDecimals(
            txChainConfig?.tokens?.usdc?.decimals ?? fallbackUsdc?.decimals ?? 7
        );

        if (!tokenIdentifier) {
            return { symbol: defaultSymbol, decimals: defaultDecimals };
        }

        if (txChainConfig && txChainConfig.tokens) {
            const raw = String(tokenIdentifier).trim();
            const evmLower = raw.toLowerCase();
            const stellarUpper = raw.toUpperCase();

            const matchByConfiguredAddress = () => {
                for (const tokenKey of Object.keys(txChainConfig.tokens)) {
                    const token = txChainConfig.tokens[tokenKey];
                    if (!token?.address) continue;
                    const cfg = String(token.address).trim();
                    if (!cfg) continue;
                    if (cfg.toLowerCase() === evmLower) return { symbol: token.symbol, decimals: normalizeTokenDecimals(token.decimals) };
                    if (cfg.toUpperCase() === stellarUpper) return { symbol: token.symbol, decimals: normalizeTokenDecimals(token.decimals) };
                }
                return null;
            };

            if (evmLower.startsWith('0x') && evmLower.length === 42) {
                const m = matchByConfiguredAddress();
                if (m) return m;
            } else if (isLikelyStellarStrkey(raw)) {
                const m = matchByConfiguredAddress();
                if (m) return m;
            }

            const sym = evmLower;
            for (const tokenKey of Object.keys(txChainConfig.tokens)) {
                const token = txChainConfig.tokens[tokenKey];
                if (token.symbol && token.symbol.toLowerCase() === sym) {
                    return { symbol: token.symbol, decimals: normalizeTokenDecimals(token.decimals) };
                }
            }
        }

        return { symbol: defaultSymbol, decimals: defaultDecimals };
    };

    const chainConfig = resolveChainConfig(chainId);
    const chainDisplayName = chainConfig?.name;

    const buildExplorerLink = (transaction) => {
        if (!transaction?.transactionHash) {
            return null;
        }

        const txChainConfig = resolveChainConfig(transaction.chainId || chainId);
        const baseUrl = txChainConfig?.blockExplorerUrl;

        if (!baseUrl) {
            return null;
        }

        const separator = baseUrl.endsWith('/') ? '' : '/';
        const txId =
            txChainConfig?.id === 'stellar'
                ? String(transaction.transactionHash)
                      .trim()
                      .replace(/^0x/i, '')
                      .toLowerCase()
                : transaction.transactionHash;
        return `${baseUrl}${separator}tx/${txId}`;
    };

    useEffect(() => {
        if (!userAccount) {
            setTransactions([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        let firestoreUnsubscribe = () => {};

        const authUnsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            firestoreUnsubscribe();
            firestoreUnsubscribe = () => {};

            if (!firebaseUser) {
                setTransactions([]);
                setLoading(false);
                return;
            }

            // EVM-only backfill (uses queryFilter + contract.provider). Stellar uses null App `provider`.
            const backfillKey = `${userAccount}-${chainId}`;
            if (contractSupportsEvmEventBackfill(contract) && !backfilledRef.current.has(backfillKey)) {
                try {
                    console.log('Starting backfill for', backfillKey);
                    const listener = new ContractListener(contract, contract.address);
                    const created = await listener.backfillReceivedTransactions(userAccount);
                    backfilledRef.current.add(backfillKey);
                    console.log(`Backfill complete for ${backfillKey}: ${created} records created`);
                } catch (error) {
                    console.error('Error backfilling received transactions:', error);
                }
            }

            const numericChainId = (() => {
                if (!chainId) return null;
                if (typeof chainId === 'string' && CHAINS[chainId]) {
                    return numericChainIdFromChainKey(chainId);
                }
                if (typeof chainId === 'string' && chainId.startsWith('0x')) {
                    return parseInt(chainId, 16);
                }
                return Number(chainId);
            })();

            firestoreUnsubscribe = RequestService.subscribeToUserRequests(
                userAccount,
                async (requests) => {
                    try {
                        console.log(
                            `Loaded ${requests.length} transactions for chain ${chainId} (numeric: ${numericChainId})`
                        );
                        const mappedRequests = requests.map((req) => {
                            const txType = mapOperationKindToType(req.operationKind, req.type);
                            const requestIdRaw = req.requestId ?? req.id;
                            const requestId =
                                requestIdRaw !== undefined && requestIdRaw !== null
                                    ? String(requestIdRaw)
                                    : '';
                            let actualRequestId = requestId;
                            if (requestId.includes(':')) {
                                const parts = requestId.split(':');
                                actualRequestId = parts[parts.length - 1];
                            }

                            return {
                                id: actualRequestId,
                                date: req.createdAt?.toDate
                                    ? formatDate(req.createdAt.toDate())
                                    : formatDate(new Date()),
                                type: txType,
                                status: req.status || 'pending',
                                amount: deriveAmountForRequest(req),
                                address:
                                    txType === 'received'
                                        ? req.details?.sender
                                        : req.details?.recipient || req.userAddress,
                                timestamp: req.createdAt?.toDate ? req.createdAt.toDate() : new Date(),
                                operation: mapOperationKindToOperation(req.operationKind),
                                transactionHash: req.transactionHash,
                                blockNumber: req.blockNumber,
                                chainId: req.chainId
                            };
                        });

                        console.log('Mapped Firestore requests:', mappedRequests.length);

                        mappedRequests.sort(
                            (a, b) => (b.timestamp?.getTime?.() || 0) - (a.timestamp?.getTime?.() || 0)
                        );

                        setTransactions(mappedRequests);
                    } catch (e) {
                        console.error('Failed mapping requests to history:', e);
                    } finally {
                        setLoading(false);
                    }
                },
                numericChainId
            );
        });

        return () => {
            authUnsubscribe();
            firestoreUnsubscribe();
        };
    }, [userAccount, contract, chainId]);

    const mapRequestsToHistoryItems = async (requests, contractInstance, provider, userAddress) => {
        // For each request, derive UI item and enrich with on-chain processed status
        const items = await Promise.all(requests.map(async (req) => {
            const base = {
                id: req.requestId || req.id,
                date: req.createdAt?.toDate ? formatDate(req.createdAt.toDate()) : formatDate(new Date()),
                type: mapOperationKindToType(req.operationKind, req.type),
                status: req.status || 'pending',
                amount: deriveAmountForRequest(req),
                address: req.details?.recipient || req.userAddress,
                timestamp: req.createdAt?.toDate ? req.createdAt.toDate() : new Date(),
                operation: mapOperationKindToOperation(req.operationKind),
                transactionHash: req.transactionHash,
                blockNumber: undefined
            };

            // Enrich from chain using requestId (txId)
            try {
                const enriched = await enrichStatusFromChain(base, req, contractInstance, provider, userAddress);
                return enriched || base;
            } catch (_) {
                return base;
            }
        }));

        // Sort newest first by timestamp if present
        items.sort((a, b) => (b.timestamp?.getTime?.() || 0) - (a.timestamp?.getTime?.() || 0));
        return items;
    };

    const mapOperationKindToOperation = (kind) => {
        switch (kind) {
            case 1: return 'deposit';
            case 2: return 'transfer';
            case 3: return 'apply';
            case 4: return 'withdraw';
            default: return 'unknown';
        }
    };

    const mapOperationKindToType = (kind, legacyType) => {
        if (typeof kind !== 'number') return (legacyType || '').toLowerCase();
        if (kind === 1) return 'deposit';
        if (kind === 2) {
            // For transfers, respect the 'received' type if explicitly set
            if (legacyType === 'received') return 'received';
            return 'send';
        }
        if (kind === 3) return 'apply';
        if (kind === 4) return 'withdraw';
        return 'unknown';
    };

    const deriveAmountForRequest = (req) => {
        // Deposits, withdrawals, and secure claim are public; transfers are confidential
        if (req.operationKind === 1 || req.operationKind === 4 || req.operationKind === 3) {
            // Get token address and chainId from request
            const tokenAddress = req.details?.tokenAddress || req.details?.token;
            const reqChainId = req.chainId || chainId;
            
            // Get token info from chain configuration
            const tokenInfo = getTokenInfoFromChain(tokenAddress, reqChainId);
            
            let amount, tokenSymbol;
            
            if (req.details?.amount && req.details?.token) {
                amount = req.details.amount;
                // Use token symbol from chain configuration instead of database value
                tokenSymbol = tokenInfo.symbol;
            } else if (req.details?.amount && req.type) {
                amount = req.details.amount;
                // Use token symbol from chain configuration
                tokenSymbol = tokenInfo.symbol;
            } else if (req.details?.pendingAmount && req.details?.token) {
                amount = req.details.pendingAmount;
                // Use token symbol from chain configuration
                tokenSymbol = tokenInfo.symbol;
            }
            
            if (amount && tokenSymbol) {
                const numericAmount = typeof amount === 'string' ? parseFloat(amount) : parseFloat(amount);
                const displayDecimals = normalizeTokenDecimals(tokenInfo.decimals);
                
                if (!isNaN(numericAmount)) {
                    // Check if amount is in raw format (amountInWei flag or integer >= 1)
                    const isRawFormat = req.details?.amountInWei === true || 
                                   (typeof amount === 'string' && amount.includes('e')) || // Scientific notation indicates large number
                                   (Number.isInteger(numericAmount) && numericAmount >= 1);
                    
                    if (isRawFormat) {
                        // Amount is in confidential plain integer units (floor(user * 10^decimals))
                        let formattedAmount = formatPlainForDisplay(numericAmount, displayDecimals);
                        
                        if (numericAmount > 0 && (formattedAmount === '' || formattedAmount === '0')) {
                            formattedAmount = plainToUserAmountNumber(numericAmount, displayDecimals).toFixed(displayDecimals);
                        }
                        
                        return `${formattedAmount} ${tokenSymbol}`;
                    } else {
                        // Legacy: Amount is in user-facing format (old records only)
                        let formattedAmount = numericAmount.toFixed(displayDecimals);
                        
                        // Remove trailing zeros
                        formattedAmount = formattedAmount.replace(/\.?0+$/, '');
                        
                        // Ensure we show at least something if value is > 0
                        if (numericAmount > 0 && (formattedAmount === '' || formattedAmount === '0')) {
                            formattedAmount = numericAmount.toFixed(displayDecimals);
                        }
                        
                        return `${formattedAmount} ${tokenSymbol}`;
                    }
                }
                return `${amount} ${tokenSymbol}`;
            }
        }
        return null; // confidential or unknown
    };

    const enrichStatusFromChain = async (base, req, contractInstance, provider, userAddress) => {
        const txId = req.requestId || req.id;
        if (!txId) return base;

        const bnTxId = ethers.BigNumber.from(txId);
        let event;
        if (base.operation === 'deposit') {
            const logs = await contractInstance.queryFilter(contractInstance.filters.DepositProcessed(userAddress, bnTxId));
            event = logs[0];
        } else if (base.operation === 'withdraw') {
            const logs = await contractInstance.queryFilter(contractInstance.filters.WithdrawProcessed(userAddress, bnTxId));
            event = logs[0];
        } else if (base.operation === 'transfer') {
            // Check both sender and recipient cases
            const logsSender = await contractInstance.queryFilter(contractInstance.filters.TransferProcessed(userAddress, null, bnTxId));
            const logsRecipient = await contractInstance.queryFilter(contractInstance.filters.TransferProcessed(null, userAddress, bnTxId));
            event = (logsSender[0] || logsRecipient[0]);
            
            // Update address based on whether user was sender or recipient
            if (logsSender[0]) {
                // User was sender, show recipient address
                base.address = logsSender[0].args.recipient;
            } else if (logsRecipient[0]) {
                // User was recipient, show sender address
                base.address = logsRecipient[0].args.sender;
            }
        } else if (base.operation === 'apply') {
            const logs = await contractInstance.queryFilter(contractInstance.filters.ApplyPendingProcessed(userAddress, bnTxId));
            event = logs[0];
        }

        if (!event) return base;

        const block = await provider.getBlock(event.blockNumber);
        const ts = new Date(block.timestamp * 1000);
        const okArg = event.args?.ok;
        return {
            ...base,
            status: okArg === false ? 'failed' : 'success',
            timestamp: ts,
            date: formatDate(ts),
            blockNumber: event.blockNumber
        };
    };

    const fetchAllTransactionEvents = async (userAddress, contractInstance, provider) => {
        try {
            console.log('Fetching transaction events for address:', userAddress);
            
            // Get current block number to limit search range
            const currentBlock = await provider.getBlockNumber();
            const fromBlock = Math.max(0, currentBlock - 10000); // Search last ~10k blocks
            
            console.log(`Searching events from block ${fromBlock} to ${currentBlock}`);
            
            // Create contract interface for event parsing
            const contractInterface = new ethers.utils.Interface(CONTRACT_ABI);
            
            // Fetch all relevant events in parallel
            const [
                depositRequested,
                depositProcessed,
                transferRequested,
                transferProcessed,
                withdrawRequested,
                withdrawProcessed,
                applyPendingRequested,
                applyPendingProcessed
            ] = await Promise.all([
                contractInstance.queryFilter(
                    contractInstance.filters.DepositRequested(userAddress),
                    fromBlock,
                    currentBlock
                ),
                contractInstance.queryFilter(
                    contractInstance.filters.DepositProcessed(userAddress),
                    fromBlock,
                    currentBlock
                ),
                contractInstance.queryFilter(
                    contractInstance.filters.TransferRequested(userAddress),
                    fromBlock,
                    currentBlock
                ),
                contractInstance.queryFilter(
                    contractInstance.filters.TransferProcessed(null, userAddress), // As recipient
                    fromBlock,
                    currentBlock
                ),
                contractInstance.queryFilter(
                    contractInstance.filters.WithdrawRequested(userAddress),
                    fromBlock,
                    currentBlock
                ),
                contractInstance.queryFilter(
                    contractInstance.filters.WithdrawProcessed(userAddress),
                    fromBlock,
                    currentBlock
                ),
                contractInstance.queryFilter(
                    contractInstance.filters.ApplyPendingRequested(userAddress),
                    fromBlock,
                    currentBlock
                ),
                contractInstance.queryFilter(
                    contractInstance.filters.ApplyPendingProcessed(userAddress),
                    fromBlock,
                    currentBlock
                )
            ]);

            console.log('Raw events found:', {
                depositRequested: depositRequested.length,
                depositProcessed: depositProcessed.length,
                transferRequested: transferRequested.length,
                transferProcessed: transferProcessed.length,
                withdrawRequested: withdrawRequested.length,
                withdrawProcessed: withdrawProcessed.length,
                applyPendingRequested: applyPendingRequested.length,
                applyPendingProcessed: applyPendingProcessed.length
            });

            // Process and combine all events
            const allTransactions = [];

            // Process deposits
            for (const event of depositProcessed) {
                const args = event.args;
                const tokenInfo = getTokenInfoFromChain(args.token, chainId);
                const amount = formatPlainForDisplay(args.plainAmount, tokenInfo.decimals);
                
                // Get actual block timestamp
                const block = await provider.getBlock(event.blockNumber);
                const timestamp = new Date(block.timestamp * 1000);
                
                allTransactions.push({
                    id: `${event.transactionHash}-${event.logIndex}`,
                    date: formatDate(timestamp),
                    type: 'deposit',
                    status: args.ok ? 'success' : 'failed',
                    amount: `${amount} ${tokenInfo.symbol}`,
                    address: userAddress,
                    timestamp: timestamp,
                    operation: 'deposit',
                    transactionHash: event.transactionHash,
                    blockNumber: event.blockNumber
                });
            }

            // Process transfers (confidential - no amounts shown)
            for (const event of transferRequested) {
                const args = event.args;
                const block = await provider.getBlock(event.blockNumber);
                const timestamp = new Date(block.timestamp * 1000);
                
                allTransactions.push({
                    id: `${event.transactionHash}-${event.logIndex}`,
                    date: formatDate(timestamp),
                    type: 'send',
                    status: 'pending', // Will be updated when processed
                    amount: null, // Confidential transfer
                    address: args.recipient,
                    timestamp: timestamp,
                    operation: 'transfer',
                    transactionHash: event.transactionHash,
                    blockNumber: event.blockNumber
                });
            }

            // Update transfer status from processed events
            for (const event of transferProcessed) {
                const args = event.args;
                const existingTx = allTransactions.find(tx => 
                    tx.transactionHash === event.transactionHash && tx.operation === 'transfer'
                );
                
                if (existingTx) {
                    existingTx.status = args.ok ? 'success' : 'failed';
                } else {
                    // Transfer received (user was recipient)
                    const block = await provider.getBlock(event.blockNumber);
                    const timestamp = new Date(block.timestamp * 1000);
                    
                    allTransactions.push({
                        id: `${event.transactionHash}-${event.logIndex}`,
                        date: formatDate(timestamp),
                        type: 'received',
                        status: args.ok ? 'success' : 'failed',
                        amount: null, // Confidential transfer
                        address: args.sender,
                        timestamp: timestamp,
                        operation: 'transfer',
                        transactionHash: event.transactionHash,
                        blockNumber: event.blockNumber
                    });
                }
            }

            // Process withdrawals
            for (const event of withdrawProcessed) {
                const args = event.args;
                const tokenInfo = getTokenInfoFromChain(args.token, chainId);
                const amount = formatPlainForDisplay(args.plainAmount, tokenInfo.decimals);
                
                const block = await provider.getBlock(event.blockNumber);
                const timestamp = new Date(block.timestamp * 1000);
                
                allTransactions.push({
                    id: `${event.transactionHash}-${event.logIndex}`,
                    date: formatDate(timestamp),
                    type: 'withdraw',
                    status: args.ok ? 'success' : 'failed',
                    amount: `${amount} ${tokenInfo.symbol}`,
                    address: userAddress,
                    timestamp: timestamp,
                    operation: 'withdraw',
                    transactionHash: event.transactionHash,
                    blockNumber: event.blockNumber
                });
            }

            // Sort by block number (most recent first)
            allTransactions.sort((a, b) => b.blockNumber - a.blockNumber);

            console.log('Processed transactions:', allTransactions.length);
            return allTransactions;

        } catch (error) {
            console.error('Error fetching transaction events:', error);
            throw error;
        }
    };

    const formatDate = (date) => {
        const dateStr = date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: '2-digit', 
            year: 'numeric' 
        });
        const timeStr = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        return `${dateStr} at ${timeStr}`;
    };

    const getTransactionIcon = (type, status) => {
        if (type === 'send') {
            return (
                <div className={`transaction-icon ${status === 'failed' ? 'failed' : 'send'}`}>
                    <i className="fas fa-arrow-up"></i>
                </div>
            );
        } else if (type === 'received') {
            return (
                <div className="transaction-icon received">
                    <i className="fas fa-arrow-down"></i>
                </div>
            );
        } else if (type === 'deposit') {
            return (
                <div className="transaction-icon deposit">
                    <i className="fas fa-arrow-down"></i>
                </div>
            );
        } else if (type === 'withdraw') {
            return (
                <div className="transaction-icon withdraw">
                    <i className="fas fa-arrow-up"></i>
                </div>
            );
        } else if (type === 'apply') {
            return (
                <div className="transaction-icon apply">
                    <i className="fas fa-check-double"></i>
                </div>
            );
        }
        return null;
    };

    const getStatusIcon = (status) => {
        if (status === 'success') {
            return <i className="fas fa-check-circle success-icon"></i>;
        } else if (status === 'failed') {
            return <i className="fas fa-times-circle failed-icon"></i>;
        }
        return null;
    };

    const getTransactionTypeText = (type, status) => {
        if (type === 'send') {
            return status === 'failed' ? 'Transaction Failed' : 'Sent';
        } else if (type === 'received') {
            return 'Received';
        } else if (type === 'deposit') {
            return 'Deposited';
        } else if (type === 'withdraw') {
            return 'Withdrawn';
        } else if (type === 'apply') {
            return 'Claimed';
        }
        return 'Unknown';
    };

    const getTransactionTypeClass = (type, status) => {
        if (status === 'failed') return 'failed';
        if (type === 'send') return 'send';
        if (type === 'received') return 'received';
        if (type === 'deposit') return 'deposit';
        if (type === 'withdraw') return 'withdraw';
        return '';
    };

    const formatAddress = (address) => {
        if (!address) return '';
        return `${address.slice(0, 6)}...${address.slice(-6)}`;
    };

    const getAddressLabel = (transaction) => {
        if (transaction.type === 'send') {
            return 'To';
        } else if (transaction.type === 'received') {
            return 'To';
        } else if (transaction.type === 'deposit' || transaction.type === 'withdraw') {
            return 'Address';
        } else if (transaction.type === 'apply') {
            return 'Account';
        }
        return 'Address';
    };

    const getProviderForChain = (txChainId) => {
        const targetChain = resolveChainConfig(txChainId);

        if (!targetChain || !targetChain.rpcUrl) {
            throw new Error(`Chain ${txChainId} not found in configuration`);
        }

        // Reuse existing provider if chain matches current selection
        if (chainConfig && targetChain.id === chainConfig.id && provider) {
            return provider;
        }
        
        return new ethers.providers.JsonRpcProvider(targetChain.rpcUrl);
    };

    const fetchRawTransactionInput = async (txProvider, txHash) => {
        try {
            const raw = await txProvider.send('eth_getTransactionByHash', [txHash]);
            if (raw && typeof raw.input === 'string') {
                return raw.input;
            }
        } catch (err) {
            console.warn('Raw eth_getTransactionByHash failed, falling back:', err?.message || err);
        }

        const tx = await txProvider.getTransaction(txHash);
        if (!tx) {
            throw new Error('Transaction not found');
        }
        return tx.data;
    };

    const resolveTransferProofBytes = async (proofArg) => {
        if (!proofArg) {
            throw new Error('Missing transfer proof bytes');
        }

        const rawHex = typeof proofArg === 'string' ? proofArg : ethers.utils.hexlify(proofArg);

        // Tempo stores proof as bytes("ipfs://<cid>") pointer; resolve it to original ABI-encoded bytes.
        try {
            const pointer = ethers.utils.toUtf8String(rawHex).trim();
            if (pointer.startsWith('ipfs://')) {
                const cid = pointer.replace('ipfs://', '').trim();
                if (!cid) {
                    throw new Error('Invalid IPFS proof pointer');
                }

                const response = await fetch(ipfsGatewayUrlForCid(cid));
                if (!response.ok) {
                    throw new Error(`Failed to fetch proof from IPFS (${response.status})`);
                }

                const proofBuffer = await response.arrayBuffer();
                return ethers.utils.hexlify(new Uint8Array(proofBuffer));
            }
        } catch (_) {
            // Not a UTF-8 pointer payload; treat as direct ABI proof bytes.
        }

        return rawHex;
    };

    const decryptTransferAmount = async (transaction) => {
        if (!transaction.transactionHash || !keypair) {
            console.error('Missing required data for decryption');
            return;
        }

        setDecryptingTxId(transaction.id);

        try {
            // Initialize WASM module
            await init();

            const txChainId = transaction.chainId || chainId;
            const txChainConfig = resolveChainConfig(txChainId);

            let tokenAddress;
            let proofData;

            if (txChainConfig?.id === 'stellar') {
                console.log('Fetching Soroban transaction:', transaction.transactionHash, 'for decrypt');
                const { tokenAddress: stellarToken, proofBytes } = await fetchStellarTransferDecryptMaterial({
                    rpcUrl: txChainConfig.rpcUrl,
                    networkPassphrase: txChainConfig.networkPassphrase,
                    gatewayContractId: txChainConfig.contractAddress,
                    txHash: transaction.transactionHash,
                });
                tokenAddress = stellarToken;
                proofData = await resolveTransferProofBytes(proofBytes);
            } else {
                const txProvider = getProviderForChain(txChainId);

                console.log('Fetching transaction:', transaction.transactionHash, 'from chain:', txChainId);
                const txInput = await fetchRawTransactionInput(txProvider, transaction.transactionHash);
                if (!txInput || txInput === '0x') {
                    throw new Error('Transaction input data not found');
                }

                const iface = new ethers.utils.Interface([
                    'function transferConfidential(address recipient, address token, bytes proof, bool useOffchainVerify)',
                ]);
                const decodedInput = iface.parseTransaction({ data: txInput });
                tokenAddress = decodedInput.args.token;
                proofData = await resolveTransferProofBytes(decodedInput.args.proof);
            }
            
            // Get token info from chain configuration
            const tokenInfo = getTokenInfoFromChain(tokenAddress, txChainId);

            // The proof is ABI-encoded as (string, string, string) - equality_proof, ciphertext_validity_proof, range_proof
            const decoded = ethers.utils.defaultAbiCoder.decode(['string', 'string', 'string'], proofData);
            const validityProofBase64 = decoded[1]; // Use the validity proof (second parameter)

            // Determine if user is sender or receiver
            // transaction.type is 'send' if user is sender, 'received' if user is receiver
            const isSender = transaction.type === 'send';
            const handleIndex = isSender ? 0 : 1; // 0 for sender, 1 for recipient
            
            console.log(`User is ${isSender ? 'sender' : 'receiver'}, using handle index ${handleIndex}`);

            // Decode the validity proof to extract the transfer amount ciphertext
            const proofDecodeResult = JSON.parse(decode_transfer_proof(validityProofBase64, handleIndex));
            if (!proofDecodeResult.success) {
                throw new Error('Failed to decode validity proof');
            }

            const transferAmountCiphertextBase64 = proofDecodeResult.transfer_amount_ciphertext;

            // Decrypt the transfer amount directly
            const transferAmountResult = JSON.parse(decrypt_ciphertext(transferAmountCiphertextBase64, keypair.private_key));
            const transferAmountRaw = transferAmountResult.decrypted_amount;
            const d = normalizeTokenDecimals(tokenInfo.decimals);
            const transferAmount = plainToUserAmountNumber(transferAmountRaw, d);

            console.log('Decryption successful:', {
                transferAmountRaw,
                transferAmount
            });

            let formattedAmount = transferAmount.toFixed(d);
            
            // Remove trailing zeros
            formattedAmount = formattedAmount.replace(/\.?0+$/, '');
            
            // Ensure we show at least something if value is > 0
            if (transferAmount > 0 && (formattedAmount === '' || formattedAmount === '0')) {
                formattedAmount = transferAmount.toFixed(d);
            }
            
            setDecryptedAmounts(prev => ({
                ...prev,
                [transaction.id]: `${formattedAmount} ${tokenInfo.symbol}`
            }));

        } catch (error) {
            console.error('Error decrypting transfer amount:', error);
            setDecryptedAmounts(prev => ({
                ...prev,
                [transaction.id]: `Error: ${error.message || 'Decryption failed'}`
            }));
        } finally {
            setDecryptingTxId(null);
        }
    };

    const hexToBytes = (hex) => {
        const bytes = new Uint8Array(hex.length / 2);
        for (let i = 0; i < hex.length; i += 2) {
            bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
        }
        return bytes;
    };

    if (!userAccount) {
        return (
            <div className="history-container">
                <div className="history-state no-account-message">
                    <h3>Connect Your Wallet</h3>
                    <p>Please connect your wallet to view transaction history.</p>
                </div>
            </div>
        );
    }

    const formattedAccount = formatAddress(userAccount);

    return (
        <div className="history-container">
            {(formattedAccount || chainDisplayName) && (
                <div className="history-meta">
                    {formattedAccount && (
                        <span className="history-chip">
                            <i className="fas fa-wallet" aria-hidden="true"></i>
                            {formattedAccount}
                        </span>
                    )}
                    {chainDisplayName && (
                        <span className="history-chip">
                            {chainConfig?.icon ? (
                                <img 
                                    src={chainConfig.icon} 
                                    alt={chainDisplayName}
                                    style={{ width: '14px', height: '14px', objectFit: 'contain' }}
                                />
                            ) : (
                                <i className="fas fa-network-wired" aria-hidden="true"></i>
                            )}
                            {chainDisplayName}
                        </span>
                    )}
                </div>
            )}
            {loading ? (
                <div className="history-state loading-container">
                    <div className="loading-spinner">
                        <i className="fas fa-circle-notch fa-spin"></i>
                    </div>
                    <p>Loading transaction history...</p>
                </div>
            ) : transactions.length === 0 ? (
                <div className="history-state empty-state">
                    <div className="empty-icon">
                        <i className="fas fa-history"></i>
                    </div>
                    <h3>No Transactions Yet</h3>
                    <p>Your transaction history will appear here once you start making transfers.</p>
                </div>
            ) : (
                <div className="transactions-list">
                    {transactions.map((transaction) => {
                        const amountClass = transaction.amount
                            ? getTransactionTypeClass(transaction.type, transaction.status)
                            : 'confidential';
                        const explorerLink = buildExplorerLink(transaction);
                        const statusClass = (transaction.status || 'pending').toLowerCase();
                        const showStatus = false; // Hide status indicators to match design
                        const statusLabel = showStatus ? formatStatusLabel(transaction.status) : null;

                        return (
                            <div key={transaction.id} className="transaction-item">
                                <div className="transaction-date">
                                    {transaction.date}
                                </div>
                                <div className="transaction-details">
                                    <div className="transaction-main">
                                        <div className="transaction-type-section">
                                            {getTransactionIcon(transaction.type, transaction.status)}
                                            <span className={`transaction-type ${getTransactionTypeClass(transaction.type, transaction.status)}`}>
                                                {getTransactionTypeText(transaction.type, transaction.status)}
                                            </span>
                                        </div>
                                        <div className="transaction-meta">
                                            {showStatus && (
                                                <span className={`transaction-status ${statusClass}`}>
                                                    {getStatusIcon(transaction.status)}
                                                    {statusLabel}
                                                </span>
                                            )}
                                            <div className="transaction-amount-wrapper">
                                                {decryptedAmounts[transaction.id] ? (
                                                    <div className={`transaction-amount ${getTransactionTypeClass(transaction.type, transaction.status)}`}>
                                                        {decryptedAmounts[transaction.id]}
                                                    </div>
                                                ) : transaction.amount ? (
                                                    <div className={`transaction-amount ${amountClass}`}>
                                                        {transaction.amount}
                                                    </div>
                                                ) : (
                                                    <div className="transaction-amount-confidential">
                                                        <div className={`transaction-amount ${amountClass}`}>
                                                            Confidential
                                                        </div>
                                                        {(transaction.type === 'send' || transaction.type === 'received') && transaction.transactionHash && keypair && (
                                                            <button 
                                                                className="decrypt-button"
                                                                onClick={() => decryptTransferAmount(transaction)}
                                                                disabled={decryptingTxId === transaction.id}
                                                            >
                                                                {decryptingTxId === transaction.id ? 'Decrypting...' : 'Decrypt Amount'}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {transaction.type !== 'deposit' && transaction.type !== 'withdraw' && transaction.type !== 'apply' && (
                                        <div className="transaction-address">
                                            {getAddressLabel(transaction)}: {formatAddress(transaction.address)}
                                        </div>
                                    )}
                                    {transaction.transactionHash && (
                                        <div className="transaction-footnote">
                                            <span>Tx:&nbsp;</span>
                                            {explorerLink ? (
                                                <a
                                                    className="transaction-link"
                                                    href={explorerLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {formatHash(transaction.transactionHash)}
                                                </a>
                                            ) : (
                                                <span className="transaction-hash">{formatHash(transaction.transactionHash)}</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default History;
